import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';

interface ListingRow {
  id: string;
  type: string;
  title: string;
  price: number | null;
  category_id: string | null;
  city: string | null;
  images: string[] | null;
  status: string;
  created_at: string;
}

function ListingsList() {
  const { user } = useAuth();
  const [listings, setListings] = useState<ListingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('listings')
        .select('*')
        .eq('owner_id', user.id)
        .order('created_at', { ascending: false });
      if (err) throw err;
      setListings((data as ListingRow[]) || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger vos annonces.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    const { error: err } = await supabase.from('listings').delete().eq('id', id);
    setDeletingId(null);
    if (err) {
      setError(err.message);
    } else {
      setListings((prev) => prev.filter((l) => l.id !== id));
    }
  };

  const statusLabel = (status: string) => {
    if (status === 'active') return { text: 'Publiée', cls: 'bg-accent-500/10 text-accent-400' };
    if (status === 'pending') return { text: 'En attente', cls: 'bg-secondary-500/10 text-secondary-400' };
    return { text: 'Suspendue', cls: 'bg-red-500/10 text-red-400' };
  };

  const typeLabel = (type: string) => (type === 'produit' ? 'Produit' : 'Annonce');

  return (
    <DashboardLayout>
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50">Mes annonces</h1>
          <p className="text-sm text-foreground-400 mt-1">
            Publiez vos produits à vendre et vos annonces / offres.
          </p>
        </div>
        <Link
          to="/dashboard/listings/new"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm whitespace-nowrap"
        >
          <i className="ri-add-line text-lg" />
          Nouvelle annonce
        </Link>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400 mb-4">
          {error}
          <button onClick={load} className="ml-3 underline text-foreground-50">Réessayer</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10">
            <i className="ri-loader-4-line text-2xl text-primary-400 animate-spin" />
          </div>
        </div>
      ) : listings.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-primary-500/10">
            <i className="ri-megaphone-line text-3xl text-primary-400" />
          </div>
          <h2 className="font-heading font-bold text-xl text-foreground-50 mb-2">Aucune annonce</h2>
          <p className="text-sm text-foreground-400 mb-6">
            Vous n&apos;avez pas encore publié d&apos;annonce. Commencez dès maintenant.
          </p>
          <Link
            to="/dashboard/listings/new"
            className="inline-block px-6 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm"
          >
            Publier ma première annonce
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {listings.map((listing) => {
            const st = statusLabel(listing.status);
            const cover = listing.images && listing.images.length > 0 ? listing.images[0] : null;
            return (
              <div
                key={listing.id}
                className="flex items-center gap-4 p-4 rounded-xl bg-background-100 border border-background-200/70"
              >
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-background-200/60 shrink-0 flex items-center justify-center">
                  {cover ? (
                    <img src={cover} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <i className="ri-image-line text-2xl text-foreground-400" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-background-200/70 text-foreground-500 whitespace-nowrap">
                      {typeLabel(listing.type)}
                    </span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${st.cls} whitespace-nowrap`}>
                      {st.text}
                    </span>
                  </div>
                  <h3 className="font-medium text-foreground-50 truncate mt-1">{listing.title}</h3>
                  <p className="text-xs text-foreground-400 mt-0.5">
                    {listing.price ? `${listing.price.toLocaleString('fr-FR')} FCFA` : 'Gratuit'}
                    {listing.city ? ` · ${listing.city}` : ''}
                  </p>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <Link
                    to={`/dashboard/listings/${listing.id}/edit`}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-300/60 text-foreground-500 hover:text-foreground-50 hover:bg-background-200/60 transition-colors"
                    aria-label="Modifier"
                  >
                    <i className="ri-edit-line" />
                  </Link>
                  <button
                    type="button"
                    onClick={() => handleDelete(listing.id)}
                    disabled={deletingId === listing.id}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-300/60 text-foreground-500 hover:text-red-400 hover:bg-red-400/10 transition-colors disabled:opacity-50"
                    aria-label="Supprimer"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </DashboardLayout>
  );
}

export default ListingsList;